import { UploadOutlined } from '@ant-design/icons';
import { Button, Form, Input, Select, Upload } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import React from 'react';
import DrawerButtons from '../../../components/drawer-buttons';
import { formItemLayout724 } from '../../../config/formLayouts';
import { documents, translations } from '../../../config/translations';
import { showSuccess } from '../../../helpers/NotificationHelper';
import { CodebooksClient, DocumentsClient, DocumentStatusEnum, DocumentTypeEnum, DocumentVm, ProjectsClient, ProjectVm, SelectOptionVm, VerificationsClient, VerificationStatusEnum, VerificationVm } from '../../../utils/api';
import { Props } from './index';

const { TextArea } = Input;

interface State {
    isSaving: boolean;
    fileList: UploadFile[];
    projects: ProjectVm[];
    statuses: SelectOptionVm[];
    purchaseRecievedNotes: DocumentVm[];
    filteredPurchaseRecievedNotes: DocumentVm[];
    projectId: number | undefined
}

class VerificationForm extends React.Component<Props, State> {
    private formRef: any;

    public constructor(props: Props) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            isSaving: false,
            fileList: [],
            projects: [],
            statuses: [],
            purchaseRecievedNotes: [],
            filteredPurchaseRecievedNotes: [],
            projectId: props.verification?.projectId
        };
    }

    public componentDidMount = () => {
        const { verification, document } = this.props;

        this.getProjects();
        this.getStatuses();
        this.getPurchaseRecievedNotes();

        if (!verification?.id) {
            this.formRef?.current?.setFieldsValue({ projectId: document?.projectId });
            this.formRef?.current?.setFieldsValue({ documentId: document?.id });
        }
    };

    private async getProjects() {
        this.setState({
            projects: await new ProjectsClient().getAll()
        });
    }

    private getStatuses = async () => {
        this.setState({
            statuses: await new CodebooksClient().getByCodebookName('VerificationStatus'),
        });
    };

    private getPurchaseRecievedNotes = async () => {
        const { verification, document } = this.props;

        const purchaseRecievedNotes = await new DocumentsClient().getAll(
            DocumentTypeEnum.PurchaseReceivedNote,
            null,
            null,
            document?.projectId,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            [DocumentStatusEnum.Completed,
            DocumentStatusEnum.Draft,
            DocumentStatusEnum.Processing,
            DocumentStatusEnum.Processed,
            ]
        );

        this.setState({
            purchaseRecievedNotes,
            filteredPurchaseRecievedNotes: !!verification?.id ? purchaseRecievedNotes.filter(p => p.projectId === verification?.projectId) : purchaseRecievedNotes
        });
    };

    private onSubmit = async (values: any) => {
        const { verification } = this.props;
        const { fileList } = this.state;

        this.setState({
            isSaving: true,
        });

        let result;
        if (verification?.id) {
            result = await new VerificationsClient().update(
                verification?.id,
                verification?.id,
                undefined,
                values?.projectId,
                values?.documentId,
                values?.verificationStatusId,
                values?.remark,
            );
        } else {
            result = await new VerificationsClient().create(
                { data: fileList[0], fileName: fileList[0].name },
                values?.projectId,
                values?.documentId,
                values?.verificationStatusId,
                values?.remark
            );
        }

        this.handleSuccess(result);

        this.setState({
            isSaving: false,
        });
    };

    private handleSuccess = (result: VerificationVm) => {
        const { onSuccess } = this.props;

        showSuccess(translations.attachments.saveSuccess);

        onSuccess(result);
    };

    private onSelectProject = (value: any): void => {
        const { purchaseRecievedNotes } = this.state;

        this.setState({
            projectId: value,
            filteredPurchaseRecievedNotes: purchaseRecievedNotes.filter(p => p.projectId === value)
        });

        this.formRef?.current?.setFieldsValue({ documentId: undefined });
    };

    public render(): React.ReactElement {
        const { verification, onClose, document } = this.props;
        const { isSaving, fileList, projects, statuses, filteredPurchaseRecievedNotes, projectId } = this.state;

        const filePickerComponent = (
            <Form.Item
                name="file"
                label={translations.verifications.verification}
                rules={[
                    {
                        required: true,
                        message: translations.general.requiredField,
                    },
                ]}
            >
                <Upload
                    fileList={fileList}
                    onRemove={() => {
                        this.setState({
                            fileList: [],
                        });
                    }}
                    beforeUpload={(file) => {
                        this.setState({
                            fileList: [file],
                        });
                        return false;
                    }}
                >
                    <Button type="primary">
                        <UploadOutlined />
                        {translations.general.upload}
                    </Button>
                </Upload>
            </Form.Item>
        );

        const projectComponent = (
            <Form.Item
                name="projectId"
                label={translations.verifications.project}
                initialValue={verification?.projectId}
                rules={[
                    {
                        required: true,
                        message: translations.general.requiredField,
                    },
                ]}
            >
                <Select showSearch allowClear optionFilterProp="children" onChange={this.onSelectProject} disabled={!!document?.projectId || !!verification?.id}>
                    {projects.map((p: ProjectVm) => (
                        <Select.Option key={p.id} value={p.id}>
                            {p.name}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
        );

        const purchaseReceivedNoteComponent = (
            <Form.Item
                name="documentId"
                label={documents.purchaseReceivedNote.name}
                initialValue={verification?.documentId}
            >
                <Select showSearch allowClear optionFilterProp="children" disabled={!projectId}>
                    {filteredPurchaseRecievedNotes.map((p: DocumentVm) => (
                        <Select.Option key={p.id} value={p.id}>
                            {p.code}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
        );

        const remarkComponent = (
            <Form.Item
                name="remark"
                label={translations.verifications.remark}
                initialValue={verification?.remark}
            >
                <TextArea rows={4} />
            </Form.Item>
        );

        const statusComponent = (
            <Form.Item
                name="verificationStatusId"
                label={translations.verifications.status}
                initialValue={verification?.verificationStatusId ?? VerificationStatusEnum.Unverified}
                
                rules={[
                    {
                        required: true,
                        message: translations.general.requiredField,
                    },
                ]}
            >
                <Select
                    options={statuses}
                />
            </Form.Item>
        );

        return (
            <>
                <Form ref={this.formRef} onFinish={this.onSubmit} {...formItemLayout724}>
                    <DrawerButtons isSaving={isSaving} onCancelAction={onClose} />
                    {!verification?.id && (
                        filePickerComponent
                    )}
                    {projectComponent}
                    {purchaseReceivedNoteComponent}
                    {statusComponent}
                    {remarkComponent}
                </Form>
            </>

        );
    }
}

export default VerificationForm;